// plugins/analytics.client.ts
import { defineNuxtPlugin, useRuntimeConfig } from '#app';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import type { Plugin } from '@amplitude/analytics-types';
import type amplitudeConfig from '~/amplitude.config';
import { useAuth } from '~/stores/auth';
import type {
  AnalyticsService,
  AnalyticsUserProfile,
} from '~/utility/analytics/events';
import { AnalyticsEvents, UTM_KEYS } from '~/utility/analytics/events';
import { getAnalyticsPageMetadata } from '~/composables/useAnalytics';

type PublicAmplitudeConfig = typeof amplitudeConfig;
type PendingEvent = {
  event: Parameters<AnalyticsService['track']>[0];
  payload: Record<string, any>;
};

const latestWebVitals: Record<string, number> = {};

const resolveConfig = (
  runtimeConfig?: Partial<PublicAmplitudeConfig>,
): PublicAmplitudeConfig => {
  const baseConfig = {
    enabled: false,
    apiKey: '',
    serverZone: 'US',
    manualTracking: true,
    pageViewEvent: AnalyticsEvents.PAGE_VIEWED,
    // Disable automatic page view to avoid duplicate page_view/page_viewed events.
    defaultTracking: {
      pageViews: false,
      sessions: true,
      fileDownloads: true,
      formInteractions: true,
    },
  };

  return {
    ...baseConfig,
    ...runtimeConfig,
    sessionReplay: {
      enabled: false,
      sampleRate: 1,
      errorSampleRate: 1,
      ...(runtimeConfig?.sessionReplay || {}),
    },
  };
};

const isPageViewTrackedByDefault = (
  defaultTracking: boolean | Record<string, boolean>,
) => {
  if (defaultTracking === true) return true;
  if (defaultTracking === false) return false;
  return Boolean((defaultTracking as Record<string, boolean>).pageViews);
};

const createUtmEnrichmentPlugin = (): Plugin => ({
  name: 'utm_enrichment',
  type: 'enrichment',
  setup: async () => {},
  execute: async (event) => {
    const metadata = getAnalyticsPageMetadata();
    const utmProps: Record<string, string> = {};

    for (const key of UTM_KEYS) {
      const value = metadata[key];
      if (
        typeof value === 'string' &&
        value &&
        !Object.prototype.hasOwnProperty.call(event.event_properties || {}, key)
      ) {
        utmProps[key] = value;
      }
    }

    if (!Object.keys(utmProps).length) return event;

    return {
      ...event,
      event_properties: {
        ...event.event_properties,
        ...utmProps,
      },
    };
  },
});

// const setupWebVitalsTracking = async (service: AnalyticsService) => {
//   if (!process.client) return;
//   try {
//     const webVitals = await import('web-vitals');
//     const report = (metric: { name: string; value: number; rating?: string; navigationType?: string; id?: string }) => {
//       latestWebVitals[metric.name] = metric.value;
//     };

//     const navigationEntry = performance.getEntriesByType('navigation')[0] as
//       | PerformanceNavigationTiming
//       | undefined;
//     const loadEventEnd = navigationEntry?.loadEventEnd;
//     const loadStart = navigationEntry?.startTime ?? navigationEntry?.navigationStart ?? 0;
//     if (typeof loadEventEnd === 'number' && loadEventEnd > 0) {
//       latestWebVitals.page_load_time_ms = loadEventEnd - loadStart;
//     }

//     webVitals.onCLS(report);
//     webVitals.onFCP(report);
//     webVitals.onFID(report);
//     webVitals.onLCP(report);
//     webVitals.onINP(report);
//     webVitals.onTTFB(report);
//   } catch (error) {
//     if (import.meta.dev) console.warn('[Analytics] Web Vitals setup failed', error);
//   }
// };

// const instrumentNetworkCalls = (service: AnalyticsService) => {
//   if (!process.client || typeof window === 'undefined') return;

//   const patchFlag = '__analyticsFetchPatched__';
//   const win = window as typeof window & { __analyticsFetchPatched__?: boolean };
//   if (win[patchFlag]) return;
//   win[patchFlag] = true;

//   const originalFetch = window.fetch.bind(window);

//   let bufferedCalls: { pageKey: string; requests: Array<Record<string, any>> } | null = null;
//   let lastFlushedPageKey: string | null = null;

//   const getPageKey = () => {
//     const meta = getAnalyticsPageMetadata();
//     return meta.page_url || window.location.pathname || 'unknown_page';
//   };

//   const ensureBuffer = () => {
//     const key = getPageKey();
//     if (!bufferedCalls || bufferedCalls.pageKey !== key) {
//       flushBuffer();
//       bufferedCalls = { pageKey: key, requests: [] };
//     }
//   };

//   const flushBuffer = () => {
//     if (!bufferedCalls || !bufferedCalls.requests.length) return;
//     const metadata = getAnalyticsPageMetadata();
//     service.track(AnalyticsEvents.NETWORK_CALL, {
//       ...metadata,
//       total_calls: bufferedCalls.requests.length,
//       requests: bufferedCalls.requests,
//     });
//     lastFlushedPageKey = bufferedCalls.pageKey;
//     bufferedCalls = null;
//   };

//   // Flush when the page is truly being unloaded/navigation away.
//   window.addEventListener('pagehide', flushBuffer, { passive: true });
//   window.addEventListener('beforeunload', flushBuffer, { passive: true });

//   const shouldSkip = (url?: string) => {
//     if (!url) return true;
//     try {
//       const normalized = new URL(url, window.location.origin);
//       const host = normalized.host.toLowerCase();
//       const path = normalized.pathname.toLowerCase();
//       return host.includes('amplitude') || path.includes('/api/amplitude');
//     } catch {
//       return false;
//     }
//   };

//   window.fetch = async (...args) => {
//     const start = performance.now();
//     const [requestInfo, requestInit] = args as [RequestInfo | URL, RequestInit | undefined];

//     const getHeader = (key: string) => {
//       try {
//         return new Headers(requestInit?.headers || (requestInfo instanceof Request ? requestInfo.headers : undefined)).get(
//           key,
//         );
//       } catch {
//         return null;
//       }
//     };

//     const url =
//       typeof requestInfo === 'string'
//         ? requestInfo
//         : requestInfo instanceof URL
//           ? requestInfo.toString()
//           : requestInfo?.url;
//     const method =
//       requestInit?.method ||
//       (typeof requestInfo === 'object' && 'method' in (requestInfo || {}) ? (requestInfo as Request).method : undefined) ||
//       'GET';

//     if (shouldSkip(url)) {
//       return originalFetch(...args);
//     }

//     const recordNetworkCall = (status?: number, errorMessage?: string, resolvedUrl?: string) => {
//       ensureBuffer();
//       const forwardedUrl = getHeader('x-forwarded-url') || getHeader('x-target-url');
//       const trackedUrl = (() => {
//         const candidate = resolvedUrl || forwardedUrl || url;
//         try {
//           const parsed = new URL(candidate, window.location.origin);
//           // If the URL is same-origin (typical proxy), strip origin to avoid proxy domain.
//           if (parsed.origin === window.location.origin) {
//             return `${parsed.pathname}${parsed.search}`;
//           }
//           return parsed.toString();
//         } catch {
//           return candidate;
//         }
//       })();

//       bufferedCalls?.requests.push({
//         url: trackedUrl,
//         method,
//         status,
//         duration_ms: Math.round(performance.now() - start),
//         error_message: errorMessage,
//       });
//     };

//     try {
//       const response = await originalFetch(...args);
//       recordNetworkCall(response.status, undefined, response?.url);
//       return response;
//     } catch (error) {
//       const message = error instanceof Error ? error.message : String(error);
//       recordNetworkCall(undefined, message);
//       throw error;
//     }
//   };
// };

const createAnalyticsService = (options: {
  enabled: boolean;
  manualTracking: boolean;
  onProfileApplied?: (profile: AnalyticsUserProfile) => void;
}) => {
  let ready = false;
  let queuedProfile: AnalyticsUserProfile | null = null;
  const pendingEvents: PendingEvent[] = [];

  const applyProfile = (profile: AnalyticsUserProfile) => {
    if (!profile?.id) return;

    amplitude.setUserId(profile.id);
    options.onProfileApplied?.(profile);

    const maskValue = (value?: string | null) => {
      if (!value) return undefined;
      const str = String(value);
      if (str.includes('@')) {
        const [local, domain] = str.split('@');
        const maskedLocal =
          local.length <= 2 ? '*'.repeat(local.length) : `${local[0]}***${local[local.length - 1]}`;
        return `${maskedLocal}@${domain}`;
      }
      if (/^\d+$/.test(str)) {
        return str.length <= 4 ? '*'.repeat(str.length) : `${'*'.repeat(str.length - 2)}${str.slice(-2)}`;
      }
      if (str.length <= 2) return '*'.repeat(str.length);
      return `${str[0]}***${str[str.length - 1]}`;
    };

    const identify = new amplitude.Identify();
    const setField = (key: string, value: unknown) => {
      if (value === undefined || value === null) return;
      identify.set(key, value);
    };
    setField('name', maskValue(profile.name));
    setField('email', maskValue(profile.email));
    setField('phone', maskValue(profile.phone));
    setField('signup_date', profile.signup_date);
    setField('user_type', profile.user_type);
    setField('country', profile.country);
    setField('city', profile.city);
    setField('total_orders', profile.total_orders);
    setField('last_order_date', profile.last_order_date);
    amplitude.identify(identify);
  };

  const service: AnalyticsService = {
    track: (event, payload) => {
      if (!options.enabled || !options.manualTracking) return;

      const eventPayload = payload ? { ...payload } : {};

      if (!ready) {
        pendingEvents.push({ event, payload: eventPayload });
        return;
      }

      amplitude.track(event, eventPayload);
    },
    setUserProfile: (profile) => {
      if (!options.enabled || !profile?.id) return;

      if (!ready) {
        queuedProfile = profile;
        return;
      }

      applyProfile(profile);
    },
    isEnabled: () => options.enabled,
    isReady: () => ready,
    reset: () => {
      if (!options.enabled) return;
      amplitude.reset();
    },
    flush: async () => {
      if (!options.enabled || !ready) return;
      await amplitude.flush();
    },
  };

  const markReady = () => {
    ready = true;

    if (queuedProfile) {
      applyProfile(queuedProfile);
      queuedProfile = null;
    }

    while (pendingEvents.length) {
      const { event, payload } = pendingEvents.shift()!;
      amplitude.track(event, payload);
    }
  };

  return { service, markReady };
};

export default defineNuxtPlugin(async (nuxtApp) => {
  const runtimeAmplitudeConfig = useRuntimeConfig().public
    ?.amplitude as Partial<PublicAmplitudeConfig> | undefined;

  const config = resolveConfig(runtimeAmplitudeConfig);
  const enabled = Boolean(process.client && config.enabled && config.apiKey);
  const verificationUrl = '/analyticsverify';

  let replayPluginInstalled = false;
  const logReplaySkip = (reason: string) => {
    if (import.meta.dev) console.debug(`[Amplitude][Replay] ${reason}`);
  };
  const verifyReplayAccess = async (normalizedUserId: string): Promise<boolean> => {
    try {
      const { data, error } = await useAPIHandler('post', verificationUrl, { auth: true });

      if (error) {
        logReplaySkip(`Verification API failed: ${error?.message || error}`);
        return false;
      }

      const response = (data || {}) as { Whitelisted?: boolean; whitelisted?: boolean; success?: boolean };

      return Boolean(response.success || response.Whitelisted || response.whitelisted);
    } catch (error) {
      logReplaySkip(`Verification API failed: ${error}`);
      return false;
    }
  };

  const tryInstallReplayPlugin = async (userId?: string | number | null) => {
    if (replayPluginInstalled) return;
    if (!config.sessionReplay?.enabled) {
      logReplaySkip('Replay disabled via config.');
      return;
    }

    const normalizedUserId = userId != null ? String(userId).trim() : '';

    // Only install when a user ID is present and explicitly allowed
    if (!normalizedUserId) {
      logReplaySkip('Replay skipped: user id missing.');
      return;
    }

    // First try API-based verification if configured
    const verified = await verifyReplayAccess(normalizedUserId);
    if (!verified) {
      logReplaySkip(`Replay skipped: verification API denied user ${normalizedUserId}.`);
      return;
    }

    const replayPlugin = sessionReplayPlugin({
      sampleRate: Number(config.sessionReplay.sampleRate ?? 0) || 0,
      errorSampleRate: Number(config.sessionReplay.errorSampleRate ?? 0) || 0,
    });

    amplitude.add(replayPlugin);
    replayPluginInstalled = true;
  };

  const { service, markReady } = createAnalyticsService({
    enabled,
    manualTracking: config.manualTracking,
    onProfileApplied: (profile) => tryInstallReplayPlugin(profile.id),
  });

  const syncUserProfileOnce = () => {
    if (!process.client) return;
    try {
      const auth = useAuth();
      if (!auth?.access_token || !auth.user?.id) return;
      service.setUserProfile({
        id: String(auth.user.id),
        name: auth.user.first_name ?? undefined,
        email: auth.user.email ?? undefined,
        phone: auth.user.phone ?? undefined,
      });
    } catch {
      // ignore
    }
  };

  // Expose as $analytics in app
  nuxtApp.provide('analytics', service);

  // Nothing else to do if disabled
  if (!enabled) return;

  const getPageViewPayload = () => {
    const metadata = getAnalyticsPageMetadata();
    return metadata;
  };

  try {
    await amplitude
      .init(config.apiKey, undefined, {
        defaultTracking: config.defaultTracking,
        serverZone: config.serverZone,
        ...(config.domainProxyUrl
          ? { serverUrl: config.domainProxyUrl }
          : {}),
      })
      .promise;

    amplitude.add(createUtmEnrichmentPlugin());

    markReady();
    syncUserProfileOnce();
  
    // Network event tracking disabled per request.
    // instrumentNetworkCalls(service);

    /**
     * Manual page view tracking:
     * - Only if manualTracking = true
     * - Custom pageViewEvent is set
     * - Page views are NOT already tracked by defaultTracking
     */
    const shouldTrackManualPageViews =
      config.manualTracking &&
      Boolean(config.pageViewEvent) &&
      !isPageViewTrackedByDefault(config.defaultTracking);

    if (shouldTrackManualPageViews && nuxtApp.$router) {
      nuxtApp.$router.afterEach((to) => {
        if (!process.client) return;
        if (to?.meta?.skipAutoPageView) return;
        service.track(config.pageViewEvent, getPageViewPayload());
      });
    }
  } catch (error) {
    console.error('[Amplitude] init failed', error);
  }
});
